import React, { useRef, useState } from 'react';
import FormButton from '@app/component/form/button/FormButton';
import CompetitorWeightsDialog from './CompetitorWeightsDialog';
import useSecurity from '@util/SecurityHook';
import Authority from '@app/model/authentication/Authority';
import ACLAuthority from '@app/model/profile/ACLAuthority';
import Domain from '@app/model/_common/Domain';
import useAppForm from '@app/site/_form';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import * as z from 'zod/v4';
import { SubmitButton } from '@app/site/_form/button/SubmitButton';
import { ResetButton } from '@app/site/_form/button/ResetButton';
import {
	useSaveCompanyHotelCalendarSettingsMutation,
} from '@app/model/company_hotel/service/CompanyHotelSettingsServiceHooks';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import CalendarSettingsDefaultDisplayType from '@app/model/company_hotel/settings/CalendarSettingsDefaultDisplayType';
import CalendarSettingsComparisonMethod from '@app/model/company_hotel/settings/CalendarSettingsComparisonMethod';
import { CalendarRoomDisplayType } from '@app/model/company_hotel/CalendarRoomDisplayType';
import CalendarSettingsDayPopupTriggerType
	from '@app/model/company_hotel/settings/CompanyHotelSettingsCalendarDayPopupTriggerType';
import CalendarSettingsPriceMedianIndicationType
	from '@app/model/company_hotel/settings/CompanyHotelSettingsPriceMedianIndicationType';
import CalendarSettingsMarketStateGroupEvaluationMethod
	from '@app/model/company_hotel/MarketStateGroupEvaluationMethod';
import CalendarSettingsPriceRecommendationIndicatorType
	from '@app/model/company_hotel/settings/CalendarSettingsPriceRecommendationIndicatorType';


const RateshopperCalendarSettingsPage = React.memo(() => {

	const { companyHotel } = useSelectedCompanyHotel();
	const toaster = useToaster();
    const security = useSecurity();
    const canWrite = security.hasAnyAuthority([Authority.ADMIN]) || (security.principal?.accessControl?.some(acl => acl.companyHotelId === companyHotel.id
        && acl.domainAuthorities.some(da => da.domain === Domain.RATESHOPPER && da.authority === ACLAuthority.WRITE)) ?? false);
    const [weightDialog, setWeightDialog] = useState<'select' | 'manage' | null>(null);
	const previousComparisonMethod = useRef(companyHotel.settings.calendar.comparisonMethod ?? CalendarSettingsComparisonMethod.MEDIAN);

	const FormValidation = z.object({
		competitorWeights: z.array(z.object({competitorId: z.number(), weight: z.number().int().min(0).max(10)})),
		defaultDisplayType: z.enum([CalendarSettingsDefaultDisplayType.NONE, CalendarSettingsDefaultDisplayType.AVAILABILITY,
            CalendarSettingsDefaultDisplayType.RANKING, CalendarSettingsDefaultDisplayType.SOLD_OUT]),
		comparisonMethod: z.enum(Object.values(CalendarSettingsComparisonMethod)),
		calendarDayRoomDisplayType: z.enum(Object.values(CalendarRoomDisplayType)),
		calendarDayPopupTriggerType: z.enum(Object.values(CalendarSettingsDayPopupTriggerType)),
		priceMedianIndicationType: z.enum(Object.values(CalendarSettingsPriceMedianIndicationType)),
		marketStateGroupEvaluationMethod: z.enum(Object.values(CalendarSettingsMarketStateGroupEvaluationMethod)),
		priceRecommendationIndicator: z.enum(Object.values(CalendarSettingsPriceRecommendationIndicatorType)),
		showOtherRoomTypeRecommendation: z.boolean(),
		numberOfDaysForPriceParity: z.number(),
	});

	const saveCalendarSettingsMutation = useSaveCompanyHotelCalendarSettingsMutation({
		onSuccess: () => {
			toaster.success('Settings', 'Calendar settings saved successfully');
		},
	});

	const form = useAppForm({
		defaultValues: {
			competitorWeights: companyHotel.competitors.map(c => ({competitorId: c.id, weight: companyHotel.settings.calendar.competitorWeights?.find(w => w.competitorId === c.id)?.weight ?? 1})),
			defaultDisplayType: toDayIndicator(companyHotel.settings.calendar.defaultDisplayType),
			comparisonMethod: companyHotel.settings.calendar.comparisonMethod ?? CalendarSettingsComparisonMethod.MEDIAN,
			calendarDayRoomDisplayType: companyHotel.settings.calendar.calendarDayRoomDisplayType,
			calendarDayPopupTriggerType: companyHotel.settings.calendar.calendarDayPopupTriggerType,
			priceMedianIndicationType: companyHotel.settings.calendar.priceMedianIndicationType,
			marketStateGroupEvaluationMethod: companyHotel.settings.calendar.marketStateGroupEvaluationMethod,
			numberOfDaysForPriceParity: companyHotel.settings.calendar.numberOfDaysForPriceParity,
			priceRecommendationIndicator: companyHotel.settings.calendar.priceRecommendationIndicator,
			showOtherRoomTypeRecommendation: companyHotel.settings.calendar.showOtherRoomTypeRecommendation,
		},
		validators: {
			onChange: FormValidation,
		},
		onSubmit: ({ value }) => canWrite ? saveCalendarSettingsMutation.mutateAsync({
			companyHotelId: companyHotel.id,
			input: value,
		}).catch((e: string) => toaster.error('Settings', e)) : undefined,

	});

	return <form.AppForm>
		<form onSubmit={(e) => {
			e.preventDefault();
			e.stopPropagation();
		}}>
			<div className="bg-white p-4 sm:p-10 rounded-xl">
				<div><h2 className="text-base font-semibold leading-5 text-gray-900">Calendar</h2>

					<fieldset className="mt-3 border-t border-gray-200 space-y-8">
						<form.AppField name="comparisonMethod">
							{(field) => <div className="mt-6">
								<p className="text-sm leading-6 text-gray-500 mb-2">Configure the price that your price is compared with.</p>
								<div className="flex-wrap space-y-4 sm:flex sm:items-center sm:space-x-4 sm:space-y-0">
									<field.RadioField disabled={!canWrite} value={CalendarSettingsComparisonMethod.MEDIAN} label="Median" />
									<field.RadioField disabled={!canWrite} value={CalendarSettingsComparisonMethod.WEIGHTED_AVERAGE} label="Weighted average" onChange={() => {
										previousComparisonMethod.current = field.state.value;
										field.handleChange(CalendarSettingsComparisonMethod.WEIGHTED_AVERAGE);
										setWeightDialog('select');
									}} />
									<FormButton state="text" isLink className="block text-sm font-medium" onClick={() => setWeightDialog('manage')}>Manage weights</FormButton>
								</div>
							</div>}
						</form.AppField>
						<form.AppField name="defaultDisplayType">
							{(field) => <div>
								<p className="text-sm leading-6 text-gray-500 mb-2">Configure the extra indicator of the calendar day.</p>
								<div className="flex-wrap space-y-4 sm:flex sm:items-center sm:space-x-4 sm:space-y-0">
									<field.RadioField disabled={!canWrite} value={CalendarSettingsDefaultDisplayType.NONE} label="None" />
									<field.RadioField disabled={!canWrite} value={CalendarSettingsDefaultDisplayType.AVAILABILITY} label="Availability" />
									<field.RadioField disabled={!canWrite} value={CalendarSettingsDefaultDisplayType.RANKING} label="Ranking" />
									<field.RadioField disabled={!canWrite} value={CalendarSettingsDefaultDisplayType.SOLD_OUT} label="Sold Out" />
								</div>
							</div>}
						</form.AppField>
						<form.AppField name="calendarDayRoomDisplayType">
							{(field) => <div>
								<p className="text-sm leading-6 text-gray-500 mb-2">Configure your calendar to show room mapping or room
									name.</p>
								<div className="flex-wrap space-y-4 sm:flex sm:items-center sm:space-x-4 sm:space-y-0">
									<field.RadioField disabled={!canWrite} value={CalendarRoomDisplayType.ROOM_MAPPING} label="Room Mapping" />
									<field.RadioField disabled={!canWrite} value={CalendarRoomDisplayType.ROOM_NAME} label="Room Name" />
								</div>
							</div>}
						</form.AppField>
						<form.AppField name="calendarDayPopupTriggerType">
							{(field) => <div>
								<p className="text-sm leading-6 text-gray-500 mb-2">Configure how the calendar day popup should be
									triggered.</p>
								<div className="flex-wrap space-y-4 sm:flex sm:items-center sm:space-x-4 sm:space-y-0">
									<field.RadioField disabled={!canWrite} value={CalendarSettingsDayPopupTriggerType.ON_CLICK} label="On Click" />
									<field.RadioField disabled={!canWrite} value={CalendarSettingsDayPopupTriggerType.ON_HOVER} label="On Hover" />
								</div>
							</div>}
						</form.AppField>
						<form.AppField name="priceMedianIndicationType">
							{(field) => <div>
								<p className="text-sm leading-6 text-gray-500 mb-2">Configure how to indicate the median price
									deviation.</p>
								<div className="flex-wrap space-y-4 sm:flex sm:items-center sm:space-x-4 sm:space-y-0">
									<field.RadioField disabled={!canWrite} value={CalendarSettingsPriceMedianIndicationType.ICON} label="Only Icon" />
									<field.RadioField disabled={!canWrite} value={CalendarSettingsPriceMedianIndicationType.ICON_AND_BACKGROUND}
									label="Icon and Background Color" />
								</div>
							</div>}
						</form.AppField>
						<form.AppField name="priceRecommendationIndicator">
							{(field) => <div>
								<p className="text-sm leading-6 text-gray-500 mb-2">Configure how to indicate the price recommendation.</p>
								<div className="flex-wrap space-y-4 sm:flex sm:items-center sm:space-x-4 sm:space-y-0">
									<field.RadioField disabled={!canWrite} value={CalendarSettingsPriceRecommendationIndicatorType.AMOUNT} label="Amount" />
									<field.RadioField disabled={!canWrite} value={CalendarSettingsPriceRecommendationIndicatorType.PERCENTAGE}
									label="Percentage" />
								</div>
							</div>}
						</form.AppField>
						<form.AppField name="showOtherRoomTypeRecommendation">
							{(field) => <div>
								<p className="text-sm leading-6 text-gray-500 mb-2">Configure the recommendation of another room type. When the cheapest room has no recommendation, the calendar and the table show the percent of the cheapest recommended room type, marked with *.</p>
								<div className="flex-wrap space-y-4 sm:flex sm:items-center sm:space-x-4 sm:space-y-0">
									<field.RadioField disabled={!canWrite} value={true} label="Show with *" />
									<field.RadioField disabled={!canWrite} value={false} label="Hide" />
								</div>
							</div>}
						</form.AppField>
						<form.AppField name="marketStateGroupEvaluationMethod">
							{(field) => <div>
								<p className="text-sm leading-6 text-gray-500 mb-2">Configure the calculation method of the flexi
									sets.</p>
								<div className="flex-wrap space-y-4 sm:flex sm:items-center sm:space-x-4 sm:space-y-0">
									<field.RadioField disabled={!canWrite} value={CalendarSettingsMarketStateGroupEvaluationMethod.AVERAGE} label="Average" />
									<field.RadioField disabled={!canWrite} value={CalendarSettingsMarketStateGroupEvaluationMethod.MAX} label="Max" />
									<field.RadioField disabled={!canWrite} value={CalendarSettingsMarketStateGroupEvaluationMethod.MIN} label="Min" />
								</div>
							</div>}
						</form.AppField>
						<form.AppField name="numberOfDaysForPriceParity">
							{(field) => <div>
								<p className="text-sm leading-6 text-gray-500 mb-2">Set the number of past days to use for price &
									availability comparison.</p>
								<div className="flex-wrap space-y-4 sm:flex sm:items-center sm:space-x-4 sm:space-y-0">
									<field.SelectField disabled={!canWrite} options={[
										{ id: '3', value: 3, label: '3 Days' },
										{ id: '7', value: 7, label: '7 Days' },
										{ id: '15', value: 15, label: '15 Days' },
										{ id: '30', value: 30, label: '30 Days' },
									]} />
								</div>
							</div>}
						</form.AppField>
					</fieldset>
					<div className="flex items-center justify-end gap-x-4 border-t border-gray-200 pt-4 mt-6">
						<ResetButton>Reset</ResetButton>
						<SubmitButton disabled={!canWrite} state="primary">Save</SubmitButton>
					</div>
				</div>
			</div>
		</form>
        {weightDialog !== null && <CompetitorWeightsDialog hotel={companyHotel} weights={form.getFieldValue('competitorWeights')}
            canWrite={canWrite} onClose={() => {
                if (weightDialog === 'select') form.setFieldValue('comparisonMethod', previousComparisonMethod.current);
                setWeightDialog(null);
            }} onApply={weights => {
                form.setFieldValue('competitorWeights', weights);
                if (weightDialog === 'select') form.setFieldValue('comparisonMethod', CalendarSettingsComparisonMethod.WEIGHTED_AVERAGE);
                setWeightDialog(null);
            }} />}
	</form.AppForm>;
});

// Before the split, MEDIAN and WEIGHTED_AVERAGE lived in this field; they show as no Day Indicator.
const toDayIndicator = (value: CalendarSettingsDefaultDisplayType): CalendarSettingsDefaultDisplayType =>
	value === CalendarSettingsDefaultDisplayType.MEDIAN || value === CalendarSettingsDefaultDisplayType.WEIGHTED_AVERAGE
		? CalendarSettingsDefaultDisplayType.NONE : value;

export default RateshopperCalendarSettingsPage;
